// @ts-nocheck
import React, { useState, useMemo, useEffect, useRef } from 'react';
import papers from './data/edexcel.json';
import PotatoGame from './PotatoGame';
import Footer from './Footer';

export default function EdexcelPapers() {
    const availableSubjects = [
        'All Subjects',
        'Accounting',
        'Arabic',
        'Biology',
        'Business',
        'Chemistry',
        'Decision Maths',
        'Economics',
        'English Lang',
        'English Lit',
        'French',
        'Further Pure Maths',
        'Geography',
        'German',
        'Greek',
        'History',
        'IT',
        'Law',
        'Mechanics',
        'Physics',
        'Psychology',
        'Pure Mathematics',
        'Spanish',
        'Statistics'
    ];

    // route kept for parity with Cambridge component (App.tsx chooses which component to render)
    const [route, setRoute] = useState<string>(window.location.hash || '#/edexcelalevelpapers');

    useEffect(() => {
        const onHash = () => setRoute(window.location.hash || '#/edexcelalevelpapers');
        window.addEventListener('hashchange', onHash);
        return () => window.removeEventListener('hashchange', onHash);
    }, []);

    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState<{ key: string | null; direction: 'asc' | 'desc' }>(
        { key: 'year', direction: 'desc' }
    );

    const [selectedSubject, setSelectedSubject] = useState<string>(availableSubjects[0]);
    const [showUnitDropdown, setShowUnitDropdown] = useState(false);
    const [showYearDropdown, setShowYearDropdown] = useState(false);
    const [showSeasonDropdown, setShowSeasonDropdown] = useState(false);

    // for Edexcel we treat "unit" as the paper identifier
    const [selectedUnits, setSelectedUnits] = useState<Array<number | string>>([]);
    const [selectedYears, setSelectedYears] = useState<number[]>([]);
    const [selectedSeasons, setSelectedSeasons] = useState<string[]>([]);

    const [showGradeBoundaries, setShowGradeBoundaries] = useState<boolean>(() => {
        try {
            const raw = window.localStorage.getItem('showGradeBoundaries');
            return raw !== null ? JSON.parse(raw) : true;
        } catch {
            return true;
        }
    });

    const unitRef = useRef(null);
    const yearRef = useRef(null);
    const seasonRef = useRef(null);

    const allData = useMemo(() => (Array.isArray(papers) ? papers : []), []);

    const availableUnits = useMemo(() => {
        // derive unit list from JSON if available for better UX
        const units = Array.from(new Set(allData.map((r: any) => r.unit))).filter(u => u !== undefined && u !== null);
        return units.length ? units.sort((a: any, b: any) => (a < b ? -1 : a > b ? 1 : 0)) : [1, 2, 3, 4, 5, 6, 7];
    }, [allData]);

    const availableYears = [2025, 2024, 2023, 2022, 2021, 2020, 2019, 2018, 2017, 2016, 2015, 2014, 2013, 2012, 2011, 2010];
    const availableSeasons = ['January', 'June', 'October'];

    // close dropdowns on outside click / escape
    useEffect(() => {
        const onClick = (e: MouseEvent) => {
            if (unitRef.current && !unitRef.current.contains(e.target)) setShowUnitDropdown(false);
            if (yearRef.current && !yearRef.current.contains(e.target)) setShowYearDropdown(false);
            if (seasonRef.current && !seasonRef.current.contains(e.target)) setShowSeasonDropdown(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setShowUnitDropdown(false);
                setShowYearDropdown(false);
                setShowSeasonDropdown(false);
            }
        };
        document.addEventListener('mousedown', onClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onClick);
            document.removeEventListener('keydown', onKey);
        };
    }, []);

    useEffect(() => {
        try {
            window.localStorage.setItem('showGradeBoundaries', JSON.stringify(showGradeBoundaries));
        } catch { }
    }, [showGradeBoundaries]);

    const toggleArray = <T,>(arr: T[], set: (v: T[]) => void, val: T) => {
        set(arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val]);
    };

    // filter uses row.unit (Edexcel JSON)
    const filteredData = useMemo(() => {
        return allData.filter((row: any) => {
            if (selectedSubject && selectedSubject !== 'All Subjects' && row.subject !== selectedSubject) return false;
            if (selectedUnits.length > 0 && !selectedUnits.includes(row.unit)) return false;
            if (selectedYears.length > 0 && !selectedYears.includes(row.year)) return false;
            if (
                selectedSeasons.length > 0 &&
                !selectedSeasons.map(s => s.toLowerCase()).includes(String(row.season || '').toLowerCase())
            )
                return false;
            return true;
        });
    }, [allData, selectedSubject, selectedUnits, selectedYears, selectedSeasons]);

    const difficultyRank = (val: any) => {
        if (val === undefined || val === null) return 0;
        const s = String(val).trim().toLowerCase();
        if (s.includes('hard')) return 3;
        if (s.includes('medium')) return 2;
        if (s.includes('easy')) return 1;
        return 0;
    };

    const seasonOrder = (season: any) => {
        const s = String(season || '').trim().toLowerCase();
        if (s.includes('january')) return 1;
        if (s.includes('june')) return 2;
        if (s.includes('october')) return 3;
        return 0;
    };

    const sortedData = useMemo(() => {
        return [...filteredData].sort((a: any, b: any) => {
            // Primary sort: user-selected column (if any)
            if (sortConfig.key) {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];
                let comparison = 0;

                if (sortConfig.key === 'difficulty') {
                    const ra = difficultyRank(a.difficulty);
                    const rb = difficultyRank(b.difficulty);
                    comparison = (ra - rb) * -1; // multiply by -1 so that "desc" shows Hard -> Medium -> Easy by default
                } else {
                    // if key doesn't exist on object, fallback to undefined comparison
                    if (aVal === undefined && bVal === undefined) comparison = 0;
                    else if (aVal === undefined) comparison = sortConfig.direction === 'asc' ? 1 : -1;
                    else if (bVal === undefined) comparison = sortConfig.direction === 'asc' ? -1 : 1;
                    else {
                        // Handle numeric and string comparisons
                        if (typeof aVal === 'number' && typeof bVal === 'number') {
                            comparison = sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
                        } else {
                            // Normalize strings to compare reliably
                            if (typeof aVal === 'string' && typeof bVal === 'string') {
                                aVal = aVal.toLowerCase();
                                bVal = bVal.toLowerCase();
                            }

                            if (aVal < bVal) comparison = sortConfig.direction === 'asc' ? -1 : 1;
                            else if (aVal > bVal) comparison = sortConfig.direction === 'asc' ? 1 : -1;
                        }
                    }
                }

                if (comparison !== 0) return comparison;
            }

            // Secondary sort: Year (descending)
            const ay = typeof a.year === 'number' ? a.year : Number(a.year) || 0;
            const by = typeof b.year === 'number' ? b.year : Number(b.year) || 0;
            const yearDiff = by - ay;
            if (yearDiff !== 0) return yearDiff;

            // Tertiary sort: Season (January > June > October)
            const seasonDiff = seasonOrder(a.season) - seasonOrder(b.season);
            if (seasonDiff !== 0) return seasonDiff;

            // Quaternary sort: Unit (ascending, numeric if possible)
            const au = typeof a.unit === 'number' ? a.unit : Number(a.unit) || 0;
            const bu = typeof b.unit === 'number' ? b.unit : Number(b.unit) || 0;
            return au - bu;
        });
    }, [filteredData, sortConfig]);

    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * entriesPerPage;
        return sortedData.slice(start, start + entriesPerPage);
    }, [sortedData, currentPage, entriesPerPage]);

    const totalPages = Math.max(1, Math.ceil(sortedData.length / entriesPerPage));
    const startEntry = sortedData.length === 0 ? 0 : (currentPage - 1) * entriesPerPage + 1;
    const endEntry = Math.min(currentPage * entriesPerPage, sortedData.length);

    // Clear filters helper (resets filters and closes dropdowns)
    const handleClearFilters = () => {
        setSelectedSubject(availableSubjects[0]);
        setSelectedUnits([]);
        setSelectedYears([]);
        setSelectedSeasons([]);
        setShowUnitDropdown(false);
        setShowYearDropdown(false);
        setShowSeasonDropdown(false);
        setCurrentPage(1);
    };

    const totalVisibleColumns = showGradeBoundaries ? 6 : 3;

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) setCurrentPage(page);
    };

    const getPageNumbers = () => {
        const pages: Array<number | string> = [];
        const maxPagesToShow = 5;

        if (totalPages <= maxPagesToShow + 2) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);

            let startPage = Math.max(2, currentPage - 1);
            let endPage = Math.min(totalPages - 1, currentPage + 1);

            if (currentPage <= 3) endPage = Math.min(maxPagesToShow, totalPages - 1);
            if (currentPage >= totalPages - 2) startPage = Math.max(2, totalPages - maxPagesToShow + 1);

            if (startPage > 2) pages.push('...');

            for (let i = startPage; i <= endPage; i++) pages.push(i);

            if (endPage < totalPages - 1) pages.push('...');

            pages.push(totalPages);
        }

        return pages;
    };
    // --- end additions ---

    const handleSort = (key: string) => {
        setSortConfig(prev => {
            if (prev.key === key) return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            return { key, direction: 'asc' };
        });
    };

    const getSortIcon = (key: string) => {
        if (sortConfig.key !== key) return ' ↕';
        return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
    };

    const openLink = (url?: string) => {
        if (!url) return;
        try {
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch {
            const a = document.createElement('a');
            a.href = url;
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            a.click();
        }
    };

    const DifficultyBadge = ({ level }: { level: string }) => {
        const value = String(level || '').trim().toLowerCase();
        const cfg: any = {
            easy: { bg: 'bg-green-500', text: 'Easy' },
            medium: { bg: 'bg-amber-500', text: 'Medium' },
            hard: { bg: 'bg-red-500', text: 'Hard' },
            default: { bg: 'bg-gray-300', text: level || 'Unknown' }
        };
        const pick = cfg[value] || cfg.default;
        return (
            <span role="status" aria-label={`Difficulty ${pick.text}`} className={`difficulty-badge ${pick.bg} text-white`}>
                <span className="difficulty-text">{pick.text}</span>
            </span>
        );
    };

    if (route === '#/home') {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-left p-8">
                    <h1 className="text-4xl font-bold mb-2">
                        <a href="#/cambridgealevelpapers" className="text-red-600 hover:underline">
                            • Cambridge AS &amp; A Level
                        </a>
                    </h1>
                    <h1 className="text-4xl font-bold mb-2">
                        <a href="#/edexcelalevelpapers" className="text-red-600 hover:underline">
                            • Edexcel International A Level
                        </a>
                    </h1>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="w-full bg-white border-b py-3" style={{ position: 'relative' }}>
                {/* Left-corner Potato group */}
                <div className="top-nav-left" aria-hidden="false">
                    <a href="#/home" className="top-nav-link home-link">「PotatoPapers」</a>
                    {/* <span className="potato-badge" role="note">work in progress!</span> */}
                </div>

                <div className="max-w-7xl mx-auto px-4">
                    <nav className="top-nav flex items-center justify-center" aria-label="Top navigation">
                        <div className="top-nav-center">
                            <a href="#/cambridgealevelpapers" className="top-nav-link">Cambridge AS &amp; AL</a>
                            <span className="sep">|</span>
                            <a href="#/edexcelalevelpapers" className="top-nav-link">Edexcel International AL</a>
                            <span className="sep">|</span>
                            <a href="#/igcsepapers" className="top-nav-link"> IGCSE</a>
                            <span className="sep">|</span>
                            <span className="top-nav-link muted">AQA &amp; OCR <span className="italic text-gray-500">Coming soon!</span></span>
                        </div>
                    </nav>
                </div>
            </div>

            {/* Hero */}
            <div
                className="relative overflow-hidden"
                style={{ 
                    background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #0a0a15 100%)', 
                    minHeight: '460px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    paddingTop: '12px'
                }}
            >
                {/* Feedback Banner - Top Center */}
                <p className="text-white text-xs sm:text-sm opacity-90 mb-2 px-4 text-center" style={{ position: 'relative', zIndex: 50, pointerEvents: 'auto' }}>
                    🚀 Site in active development. Found a bug or have feedback? <a href="https://www.reddit.com/user/Available-Estate5153/" target="_blank" rel="noopener noreferrer" className="underline hover:text-yellow-300 transition-colors">DM me on Reddit!</a>
                </p>

                {/* Potato Game Background */}
                <div className="absolute inset-0 z-0">
                    {/* <PotatoGame /> */}
                    <PotatoGame />
                </div>

                <div className="relative z-10 px-4 py-20 text-center">
                    <h1 className="text-6xl font-bold text-white mb-2 tracking-wide">Edexcel IA-Level Past Year Papers</h1>
                    <p className="text-white text-lg italic opacity-80">Search for past year papers</p>

                    {/* Search Form */}
                    <div className="max-w-6xl mx-auto mt-12 px-4">
                        <div className="flex flex-wrap justify-center gap-4 mb-6">
                            <div className="text-left flex-shrink-0" style={{ minWidth: '180px' }}>
                                <label className="text-white text-sm mb-2 block">Subject</label>
                                <select
                                    value={selectedSubject}
                                    onChange={e => setSelectedSubject(e.target.value)}
                                    className="w-full px-4 py-3 bg-gray-900 bg-opacity-50 border border-gray-600 text-white rounded focus:outline-none focus:border-red-500"
                                >
                                    {availableSubjects.map(sub => (
                                        <option key={sub} value={sub}>
                                            {sub}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div ref={unitRef} className="text-left relative flex-shrink-0" style={{ minWidth: '180px' }}>
                                <label className="text-white text-sm mb-2 block">Unit(s)</label>
                                <div className="relative">
                                    <button
                                        onClick={() => setShowUnitDropdown(v => !v)}
                                        className="w-full px-4 py-3 bg-gray-900 bg-opacity-50 border border-gray-600 text-white rounded focus:outline-none focus:border-red-500 text-left flex justify-between items-center"
                                    >
                                        <span>{selectedUnits.length > 0 ? `${selectedUnits.length} selected` : 'UNIT'}</span>
                                        <span>▼</span>
                                    </button>
                                    {showUnitDropdown && (
                                        <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded shadow-lg max-h-56 overflow-y-auto pb-2">
                                            {availableUnits.map((u, idx) => (
                                                <label
                                                    key={String(u)}
                                                    className={`flex items-center px-4 py-2 hover:bg-gray-700 cursor-pointer text-white ${idx === availableUnits.length - 1 ? 'mb-2' : ''}`}

                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedUnits.includes(u)}
                                                        onChange={() => toggleArray(selectedUnits, setSelectedUnits, u)}
                                                        className="mr-2"
                                                    />
                                                    {u}
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div ref={yearRef} className="text-left relative flex-shrink-0" style={{ minWidth: '180px' }}>
                                <label className="text-white text-sm mb-2 block">Year(s)</label>
                                <div className="relative">
                                    <button
                                        onClick={() => setShowYearDropdown(v => !v)}
                                        className="w-full px-4 py-3 bg-gray-900 bg-opacity-50 border border-gray-600 text-white rounded focus:outline-none focus:border-red-500 text-left flex justify-between items-center"
                                    >
                                        <span>{selectedYears.length > 0 ? `${selectedYears.length} selected` : 'YEAR'}</span>
                                        <span>▼</span>
                                    </button>
                                    {showYearDropdown && (
                                        <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded shadow-lg max-h-56 overflow-y-auto pb-2">
                                            {availableYears.map((y, idx) => (
                                                <label key={y} className={`flex items-center px-4 py-2 hover:bg-gray-700 cursor-pointer text-white ${idx === availableYears.length - 1 ? 'mb-2' : ''}`}>
                                                    <input type="checkbox" checked={selectedYears.includes(y)} onChange={() => toggleArray(selectedYears, setSelectedYears, y)} className="mr-2" />
                                                    {y}
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div ref={seasonRef} className="text-left relative flex-shrink-0" style={{ minWidth: '180px' }}>
                                <label className="text-white text-sm mb-2 block">Season(s)</label>
                                <div className="relative">
                                    <button
                                        onClick={() => setShowSeasonDropdown(v => !v)}
                                        className="w-full px-4 py-3 bg-gray-900 bg-opacity-50 border border-gray-600 text-white rounded focus:outline-none focus:border-red-500 text-left flex justify-between items-center"
                                    >
                                        <span>{selectedSeasons.length > 0 ? `${selectedSeasons.length} selected` : 'SEASON'}</span>
                                        <span>▼</span>
                                    </button>
                                    {showSeasonDropdown && (
                                        <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded shadow-lg max-h-56 overflow-y-auto pb-2">
                                            {availableSeasons.map((s, idx) => (
                                                <label key={s} className={`flex items-center px-4 py-2 hover:bg-gray-700 cursor-pointer text-white ${idx === availableSeasons.length - 1 ? 'mb-2' : ''}`}>
                                                    <input type="checkbox" checked={selectedSeasons.includes(s)} onChange={() => toggleArray(selectedSeasons, setSelectedSeasons, s)} className="mr-2" />
                                                    {s}
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-center">
                            <button onClick={handleClearFilters} className="search-button px-16 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded transition-colors duration-200 flex items-center justify-center gap-2 text-xl" aria-label="Clear filters" title="Clear all filters">
                                <span className="text-xl">✖</span> Clear filters
                            </button>
                        </div>
                    </div>
                </div>

                {/* Disclaimer Banner - Bottom Center */}
                <div className="w-full text-center py-4 px-4" style={{ background: 'rgba(51, 51, 51, 0.3)', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <p className="text-white text-xs sm:text-sm opacity-85 max-w-4xl mx-auto">
                        Please Note: All past papers, mark schemes and related materials are © Pearson Edexcel.
                    </p>
                </div>
            </div>

            {/* Results */}
            <div className="max-w-7xl mx-auto responsive-px py-8">
                <div className="scalable-table-section">
                    <div className="mb-4 controls-row">
                        <div className="flex items-center gap-2 controls-left">
                            <span className="text-gray-700">Show</span>
                            <select
                                value={entriesPerPage}
                                onChange={e => {
                                    setEntriesPerPage(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                                className="px-3 py-1 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                            <span className="text-gray-700">entries</span>
                        </div>

                        <div className="flex items-center gap-3 controls-right">
                            <label className="flex items-center gap-2 text-gray-700 select-none grade-boundaries-toggle">
                                <input type="checkbox" checked={showGradeBoundaries}
                                    onChange={e => setShowGradeBoundaries(e.target.checked)}
                                    className="h-4 w-4" 
                                     />
                                <span>Show Grade Boundaries</span>
                            </label>
                        </div>
                    </div>

                    <div className="table-container">
                    <div className="overflow-x-auto shadow-lg rounded-lg">
                        <table className="w-full bg-white pastpapers-table">
                        <thead>
                            <tr className="bg-red-600 text-white">
                                <th onClick={() => handleSort('year')} className="px-6 py-4 text-center font-semibold cursor-pointer hover:bg-red-700 select-none text-base">
                                    Paper (sort by year){getSortIcon('year')}
                                </th>
                                <th onClick={() => handleSort('difficulty')} className="px-6 py-4 text-center font-semibold cursor-pointer hover:bg-red-700 select-none text-base">
                                    Difficulty{getSortIcon('difficulty')}
                                </th>

                                {showGradeBoundaries && (
                                    <>
                                        <th onClick={() => handleSort('gradeA')} className="px-3 py-4 text-center font-semibold cursor-pointer hover:bg-red-700 select-none text-base grade-boundary-col" style={{ width: '6%' }}>
                                            A{getSortIcon('gradeA')}
                                        </th>
                                        <th onClick={() => handleSort('gradeB')} className="px-3 py-4 text-center font-semibold cursor-pointer hover:bg-red-700 select-none text-base grade-boundary-col" style={{ width: '6%' }}>
                                            B{getSortIcon('gradeB')}
                                        </th>
                                        <th onClick={() => handleSort('gradeC')} className="px-3 py-4 text-center font-semibold cursor-pointer hover:bg-red-700 select-none text-base grade-boundary-col" style={{ width: '6%' }}>
                                            C{getSortIcon('gradeC')}
                                        </th>
                                    </>
                                )}

                                <th className="px-6 py-4 text-left font-semibold text-base">Files</th>
                            </tr>
                        </thead>

                        <tbody>
                            {paginatedData.length === 0 ? (
                                <tr>
                                    <td colSpan={totalVisibleColumns} className="px-6 py-8 text-center text-gray-500 text-base">
                                        No results found. Try adjusting your filters.
                                    </td>
                                </tr>
                            ) : (
                                paginatedData.map((row: any, idx: number) => (
                                    <tr key={`${row.code ?? idx}-${row.subject ?? ''}-${row.year ?? ''}-${row.unit ?? ''}`} className={`border-b border-gray-200 hover:bg-gray-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-100'}`}>
                                        <td className="px-6 py-4 text-base text-center">{row.code ?? ''}</td>
                                        <td className="px-6 py-4 text-base">
                                            <DifficultyBadge level={row.difficulty} />
                                        </td>

                                        {showGradeBoundaries && (
                                            <>
                                                <td className="px-3 py-4 text-center text-base grade-boundary-col">{row.gradeA ?? ''}</td>
                                                <td className="px-3 py-4 text-center text-base grade-boundary-col">{row.gradeB ?? ''}</td>
                                                <td className="px-3 py-4 text-center text-base grade-boundary-col">{row.gradeC ?? ''}</td>
                                            </>
                                        )}
                                        <td className="px-6 py-4 files-column">
                                            <div className="flex gap-2">
                                                <button onClick={() => openLink(row.questionUrl)} disabled={!row.questionUrl} className={`px-4 py-2 border-2 border-red-600 text-red-600 rounded ${row.questionUrl ? 'hover:bg-red-600 hover:text-white' : 'opacity-40 cursor-not-allowed'}`}>
                                                    QP
                                                </button>
                                                <button onClick={() => openLink(row.answerUrl)} disabled={!row.answerUrl} className={`px-4 py-2 border-2 border-red-600 text-red-600 rounded ${row.answerUrl ? 'hover:bg-red-600 hover:text-white' : 'opacity-40 cursor-not-allowed'}`}>
                                                    MS
                                                </button>
                                                <button onClick={() => openLink(row.thresholdUrl)} disabled={!row.thresholdUrl} className={`px-4 py-2 border-2 border-red-600 text-red-600 rounded ${row.thresholdUrl ? 'hover:bg-red-600 hover:text-white' : 'opacity-40 cursor-not-allowed'}`}>
                                                    ER
                                                </button>

                                                {/* YouTube logo button (matches Cambridge layout) */}
                                                <button
                                                    onClick={() => row.vidUrl && openLink(row.vidUrl)}
                                                    disabled={!row.vidUrl}
                                                    aria-label={`Open video for ${row.subject} ${row.year}`}
                                                    title="Video"
                                                    className={`rounded transition-colors duration-200 flex items-center justify-center youtube-logo-button ${row.vidUrl ? '' : 'opacity-40 cursor-not-allowed'}`}
                                                    style={{ border: 'none', background: 'transparent', padding: 0, flexShrink: 0 }}
                                                >
                                                    <img
                                                        src="/images/youtube-logo.png"
                                                        alt="YouTube"
                                                        style={{ height: '36px', width: 'auto', display: 'block', maxWidth: '100%', minWidth: '36px' }}
                                                    />
                                                    <span className="sr-only">Video</span>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                    </div>
                </div>

                {/* Pagination */}
                <div className="mt-6 flex items-center justify-between flex-wrap gap-4">
                    <div className="text-gray-700 pagination-info">
                        Showing {startEntry} to {endEntry} of {sortedData.length} entries
                    </div>

                    <div className="flex gap-2 flex-wrap">
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className={`px-4 py-2 border border-gray-300 rounded ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}>
                            Previous
                        </button>

                        {getPageNumbers().map((p, i) =>
                            p === '...' ? (
                                <span key={`el-${i}`} className="px-4 py-2 text-gray-700">...</span>
                            ) : (
                                <button key={p} onClick={() => handlePageChange(Number(p))} className={`px-4 py-2 rounded ${currentPage === p ? 'bg-red-600 text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-100'}`}>
                                    {p}
                                </button>
                            )
                        )}


                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className={`px-4 py-2 border border-gray-300 rounded ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}>
                            Next
                        </button>
                    </div>
                </div>
                </div>
            </div>

            {/* Footer */}
            <Footer />
        </div>
    );
}
